<script setup>
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import MacrosTableRow from './MacrosTableRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});
const searchQuery = ref('');

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    useAlert(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
    t('MACROS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

// Sentinel value representing macros without a prefix (displayed as "Others")
const NO_PREFIX = '__no_prefix__';

const extractPrefix = macro => {
  const name = macro?.name || '';
  const parts = name.split(':');
  if (parts.length < 2) return null;
  const prefix = parts[0].trim();
  return prefix || null;
};

const groupedRecords = computed(() => {
  const groups = {};
  const query = searchQuery.value.trim();
  const sourceRecords = query ? filteredRecords.value : records.value;

  sourceRecords.forEach(macro => {
    const prefix = extractPrefix(macro) || NO_PREFIX;
    if (!groups[prefix]) {
      groups[prefix] = [];
    }
    groups[prefix].push(macro);
  });

  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
      })
    );
  });

  const keys = Object.keys(groups).sort((a, b) => {
    if (a === NO_PREFIX && b !== NO_PREFIX) return 1;
    if (b === NO_PREFIX && a !== NO_PREFIX) return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });

  return keys.map(prefix => ({
    prefix,
    items: groups[prefix],
  }));
});

const expandedGroups = ref({});

// default: collapsed
const isGroupExpanded = prefix => {
  const val = expandedGroups.value[prefix];
  return val === true;
};

const toggleGroup = prefix => {
  expandedGroups.value[prefix] = !isGroupExpanded(prefix);
};

const groupLabel = prefix => {
  if (prefix === NO_PREFIX) {
    return t('MACROS.GROUP.OTHERS');
  }
  return prefix;
};
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('MACROS.LIST.404')"
    :no-records-found="!records.length"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('MACROS.LOADING')"
    feature-name="macros"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('MACROS.HEADER')"
        :description="$t('MACROS.DESCRIPTION')"
        :link-text="$t('MACROS.LEARN_MORE')"
        :search-placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
        feature-name="macros"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('MACROS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link :to="{ name: 'macros_new' }">
            <Button :label="$t('MACROS.HEADER_BTN_TXT')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="table-fixed w-full min-w-full divide-y divide-n-weak">
        <colgroup>
          <col class="w-[35%]" />
          <col class="w-[20%]" />
          <col class="w-[20%]" />
          <col class="w-[15%]" />
          <col class="w-[10%]" />
        </colgroup>

        <thead>
          <tr>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ thHeader }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 text-right font-semibold text-n-slate-11"
            />
          </tr>
        </thead>

        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <template v-for="group in groupedRecords" :key="group.prefix">
            <tr class="bg-n-slate-3 text-n-slate-11 py-2">
              <td class="py-2 text-sm" colspan="5">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 w-full text-left"
                  :aria-label="
                    isGroupExpanded(group.prefix)
                      ? $t('MACROS.GROUP.COLLAPSE')
                      : $t('MACROS.GROUP.EXPAND')
                  "
                  @click="toggleGroup(group.prefix)"
                >
                  <span
                    class="inline-flex items-center justify-center w-4 h-4 mr-1 rounded-sm"
                  >
                    <i
                      class="i-lucide-chevron-right text-[10px] transition-transform"
                      :class="{ 'rotate-90': isGroupExpanded(group.prefix) }"
                      aria-hidden="true"
                    />
                  </span>
                  <span class="font-medium">
                    {{ groupLabel(group.prefix) }}
                  </span>
                  <span class="text-xs text-n-slate-9 ms-auto">
                    {{
                      $t('MACROS.GROUP.ITEMS_LABEL', {
                        count: group.items.length,
                      })
                    }}
                  </span>
                </button>
              </td>
            </tr>

            <template v-if="isGroupExpanded(group.prefix)">
              <MacrosTableRow
                v-for="macro in group.items"
                :key="macro.id"
                :macro="macro"
                @delete="openDeletePopup(macro)"
              />
            </template>
          </template>
        </tbody>
      </table>
      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
        :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
        :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
      />
    </template>
  </SettingsLayout>
</template>
